import { formValueSelector } from 'redux-form';
import { AppState } from 'store';
import {
  TaxTraxFormPlaceholders,
  TAXTRAX_FORM,
  EMPTY_VALUE,
  SearchTypeFormNames,
} from './TaxTrax.constants';
import {
  AvailableMunicipalities,
  GetDriversResult,
  TaxTraxResult,
} from './models';
import { Cities, Counties, USStates, Villages } from 'common/models';

type LocationsType = {
  name: string;
  hobCode: string;
}[];

export const usStatesSelector = ({
  taxTrax: { usStates },
}: AppState): USStates => usStates;

export const showAvailableMunicipalitiesModalSelector = ({
  taxTrax: { showAvailableMunicipalitiesModal },
}: AppState): boolean => showAvailableMunicipalitiesModal;

export const availalableMunicipalitiesLoadingSelector = ({
  taxTrax: { availableMunicipalitiesLoading },
}: AppState): boolean => availableMunicipalitiesLoading;

export const availableMunicipalitiesSelector = ({
  taxTrax: { availableMunicipalities },
}: AppState): AvailableMunicipalities => availableMunicipalities;

export const resultSelector = ({
  taxTrax: { result },
}: AppState): TaxTraxResult => result;

export const driversSelector = ({
  taxTrax: { driversData },
}: AppState): GetDriversResult => driversData;

export const taxBillsResponseSelector = ({
  taxTraxTaxBills: { response },
}: AppState) => response;

export const propertySelector = ({ taxTraxTaxBills: { property } }: AppState) =>
  property;

export const detailsLoadingSelector = ({
  taxTraxTaxBills: { loading },
}: AppState) => loading;

export const taxBillRecordSelector = ({
  taxTraxTaxBillRecord: { taxBillRecord },
}: AppState) => taxBillRecord;

export const taxBillSelector = ({
  taxTraxTaxBillRecord: { taxBill },
}: AppState) => taxBill;

export const valueSelector = (state: AppState, fieldName: string) =>
  formValueSelector(TAXTRAX_FORM)(state, fieldName);

export const getCounties = (
  usStates: USStates | undefined,
  stateHob: string,
) => {
  const usState = usStates?.find(({ stateCode }) => stateCode === stateHob);
  return usState ? usState.counties : [];
};

export const getCities = (counties: Counties | null, countyHob: string) => {
  const county = counties?.find(({ hobCode }) => hobCode === countyHob);
  return county ? county.cities : [];
};

export const getVillages = (cities: Cities | null, cityHob: string) => {
  const city = cities?.find(({ hobCode }) => hobCode === cityHob);
  return city ? city.villages : [];
};

const getPlaceHolder = (text: string) => [
  {
    value: EMPTY_VALUE,
    text,
  },
];

const getOptions = (locations: LocationsType) =>
  locations.map(({ name: text, hobCode: value }) => ({ value, text }));

const getStateOptionsArray = (states: USStates) =>
  states.map(({ stateCode: text, stateCode: value }) => ({ value, text }));

export const getStateOptions = (states: USStates) =>
  states?.length > 1
    ? [
        ...getPlaceHolder(TaxTraxFormPlaceholders.STATE),
        ...getStateOptionsArray(states),
      ]
    : [...getStateOptionsArray(states)];

export const getCountyOptions = (counties: Counties) =>
  counties?.length > 1
    ? [
        ...getPlaceHolder(TaxTraxFormPlaceholders.COUNTY),
        ...getOptions(counties),
      ]
    : [...getOptions(counties)];

export const getCityOptions = (cities: Cities) => [
  ...getPlaceHolder(TaxTraxFormPlaceholders.CITY),
  ...getOptions(cities),
];

export const getVillageOptions = (villages: Villages) => [
  ...getPlaceHolder(TaxTraxFormPlaceholders.VILLAGE),
  ...getOptions(villages),
];

export const getHobCode = ({ countyHob, cityHob, villageHob }: any) =>
  villageHob || cityHob || countyHob || EMPTY_VALUE;

export const searchByAddressBody = ({
  [SearchTypeFormNames.ADDRESS_SEARCH]: {
    streetNumber = EMPTY_VALUE,
    streetName = EMPTY_VALUE,
  },
  hobCodes,
}: any) => {
  const hobCode = getHobCode(hobCodes);
  return {
    streetNumber,
    streetName,
    hob: hobCode,
  };
};

export const searchByOwnerBody = ({
  [SearchTypeFormNames.OWNER_SEARCH]: {
    firstName = EMPTY_VALUE,
    lastName = EMPTY_VALUE,
    corporationName = EMPTY_VALUE,
  },
  hobCodes,
}: any) => {
  const hobCode = getHobCode(hobCodes);
  return {
    firstName,
    lastName,
    corporationName,
    hob: hobCode,
  };
};
